import { ReactNode, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  UploadCloud,
  LogOut,
  ChevronRight,
  MessageCircle,
  Menu,
  X,
} from "lucide-react";
import { useStore } from "../../store/useStore";
import { cn } from "../../utils/helpers";
import { CommandPalette } from "../ui/CommandPalette";
import { ToastSystem } from "../ui/ToastSystem";

export const DashboardLayout = ({ children }: { children: ReactNode }) => {
  const { logout, activeTab, setTab, user } = useStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    // { id: "compare", label: "Compare", icon: Scale },
    { id: "upload", label: "Data Upload", icon: UploadCloud },
    { id: "chat", label: "AI Chat", icon: MessageCircle },
  ];

  return (
    <div className="h-screen w-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex font-sans selection:bg-indigo-500/30 overflow-hidden">
      <ToastSystem />
      <CommandPalette />

      {/* --- MOBILE SIDEBAR (Overlay) --- */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
            />
            {/* Drawer */}
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", bounce: 0, duration: 0.3 }}
              className="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col lg:hidden"
            >
              <SidebarContent
                activeTab={activeTab}
                setTab={(t) => {
                  setTab(t);
                  setIsMobileMenuOpen(false);
                }}
                logout={logout}
                closeMenu={() => setIsMobileMenuOpen(false)}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* --- DESKTOP SIDEBAR (Static) --- */}
      <aside className="hidden lg:flex w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex-col z-30 flex-shrink-0">
        <SidebarContent activeTab={activeTab} setTab={setTab} logout={logout} />
      </aside>

      {/* --- MAIN CONTENT --- */}
      <main className="flex-1 flex flex-col h-full w-full relative min-w-0">
        {/* Header */}
        <header className="h-16 flex-shrink-0 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800 px-4 lg:px-8 flex justify-between items-center z-20">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 -ml-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <span className="hidden sm:inline hover:text-indigo-600 cursor-pointer transition-colors font-medium">
                App
              </span>
              <ChevronRight size={14} className="opacity-50 hidden sm:block" />
              <span className="font-semibold text-slate-900 dark:text-slate-200 capitalize bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md text-xs tracking-wide">
                {navItems.find((n) => n.id === activeTab)?.label ||
                  (activeTab === "compare" ? "Dashboard" : activeTab)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-3 pl-2">
              <div className="text-right hidden md:block leading-tight">
                <p className="text-xs lg:text-sm font-bold text-slate-900 dark:text-white">
                  {user?.name}
                </p>
                <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-bold uppercase tracking-wider">
                  Admin
                </p>
              </div>
              <div className="w-8 h-8 rounded-full bg-slate-200 p-0.5 ring-2 ring-indigo-500/20 shadow-sm overflow-hidden flex-shrink-0">
                <img
                  src="/ddre-logo.jpeg"
                  className="w-full h-full object-cover rounded-full"
                  alt="User"
                />
              </div>
            </div>
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 lg:p-6 w-full max-w-[1920px] mx-auto scroll-smooth">
          <AnimatePresence mode="wait">{children}</AnimatePresence>
        </div>
      </main>

    </div>
  );
};

// Extracted Sidebar Content for Reusability
const SidebarContent = ({
  activeTab,
  setTab,
  logout,
  closeMenu,
}: {
  activeTab: string;
  setTab: (t: any) => void;
  logout: () => void;
  closeMenu?: () => void;
}) => {
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    // { id: "compare", label: "Compare", icon: Scale },
    { id: "upload", label: "Data Upload", icon: UploadCloud },
    { id: "chat", label: "AI Chat", icon: MessageCircle },
  ];

  return (
    <>
      <div className="h-16 flex items-center justify-between px-6 border-b border-slate-100 dark:border-slate-800/50 flex-shrink-0">
        <div className="flex items-center gap-3">
          <img
            src="/ddre-logo.jpeg"
            alt="DDRE Logo"
            className="w-8 h-8 object-contain"
          />
          <span className="font-bold text-xl tracking-tight text-slate-900 dark:text-white">
            DDRE
          </span>
        </div>
        {closeMenu && (
          <button onClick={closeMenu} className="text-slate-500">
            <X size={20} />
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 space-y-1 mt-6 overflow-y-auto custom-scrollbar">
        <div className="px-3 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest opacity-80">
          Main Menu
        </div>
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setTab(item.id)}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm font-medium group relative overflow-hidden",
              activeTab === item.id
                ? "bg-slate-100 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400"
                : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-slate-200",
            )}
          >
            {activeTab === item.id && (
              <motion.div
                layoutId="nav-pill"
                className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-indigo-600 rounded-r-full shadow-[0_0_10px_rgba(79,70,229,0.5)]"
              />
            )}
            <item.icon
              size={20}
              className={cn(
                "transition-colors relative z-10",
                activeTab === item.id && "text-indigo-600 dark:text-indigo-400",
              )}
            />
            <span className="relative z-10">{item.label}</span>
          </button>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex-shrink-0">
        <button
          onClick={logout}
          className="flex items-center gap-3 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors w-full px-4 py-2 rounded-lg group"
        >
          <LogOut
            size={20}
            className="group-hover:translate-x-1 transition-transform"
          />
          <span className="text-sm font-medium">Sign Out</span>
        </button>
      </div>
    </>
  );
};
